import { RefreshCw, Tags } from 'lucide-react';

import { MONITOR_COLUMNS, getColumnGroups } from './columns';

import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/Table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/Tooltip';
import { VisionMonitorRow } from '@/services/VisionMonitorService';

interface MonitorTableProps {
  rows: VisionMonitorRow[];
  selected: Set<number>;
  disabled: boolean;
  onToggleRow: (catalogId: number) => void;
  onTogglePage: (checked: boolean) => void;
  onReread: (catalogId: number) => void;
  onRegenerateCategory: (catalogId: number) => void;
}

export function MonitorTable({
  rows,
  selected,
  disabled,
  onToggleRow,
  onTogglePage,
  onReread,
  onRegenerateCategory,
}: MonitorTableProps) {
  const groups = getColumnGroups();
  const allSelected =
    rows.length > 0 && rows.every((row) => selected.has(row.catalogId));
  const someSelected =
    !allSelected && rows.some((row) => selected.has(row.catalogId));

  return (
    <TooltipProvider>
      <div className="rounded-lg border border-gray-200 bg-white p-2">
        <Table className="min-w-max">
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead />
              {groups.map(({ group, span, className }) => (
                <TableHead
                  key={group}
                  colSpan={span}
                  className={`h-8 border-l text-center text-xs font-bold uppercase tracking-wide ${className}`}
                >
                  {group}
                </TableHead>
              ))}
              <TableHead />
            </TableRow>
            <TableRow>
              <TableHead className="w-10">
                <Checkbox
                  aria-label="Select all rows on this page"
                  checked={allSelected || (someSelected && 'indeterminate')}
                  onCheckedChange={(checked) => onTogglePage(checked === true)}
                />
              </TableHead>
              {MONITOR_COLUMNS.map((column) => (
                <TableHead
                  key={column.key}
                  className="whitespace-nowrap font-semibold"
                >
                  {column.label}
                </TableHead>
              ))}
              <TableHead className="sticky right-0 bg-white text-center font-semibold">
                Actions
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => (
              <TableRow
                key={row.catalogId}
                data-state={
                  selected.has(row.catalogId) ? 'selected' : undefined
                }
                className="hover:bg-gray-50"
              >
                <TableCell>
                  <Checkbox
                    aria-label={`Select catalog ${row.catalogId}`}
                    checked={selected.has(row.catalogId)}
                    onCheckedChange={() => onToggleRow(row.catalogId)}
                  />
                </TableCell>
                {MONITOR_COLUMNS.map((column) => (
                  <TableCell key={column.key} className={column.className}>
                    {column.render(row)}
                  </TableCell>
                ))}
                <TableCell className="sticky right-0 bg-white">
                  <div className="flex justify-center gap-1">
                    <ActionButton
                      label="Re-read in Vision"
                      disabled={disabled}
                      onClick={() => onReread(row.catalogId)}
                      icon={<RefreshCw className="h-4 w-4" />}
                    />
                    <ActionButton
                      label="Regenerate Vision category name"
                      disabled={disabled}
                      onClick={() => onRegenerateCategory(row.catalogId)}
                      icon={<Tags className="h-4 w-4" />}
                    />
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </TooltipProvider>
  );
}

interface ActionButtonProps {
  label: string;
  disabled: boolean;
  icon: React.ReactNode;
  onClick: () => void;
}

function ActionButton({ label, disabled, icon, onClick }: ActionButtonProps) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          aria-label={label}
          disabled={disabled}
          onClick={onClick}
        >
          {icon}
        </Button>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}
